// The venue's minimum order value for Wolt delivery.
//
// A venue configures `WoltDeliveryMinOrderValue` (major units, 0 = no minimum);
// /mobile/restaurant/fetchspecific returns it so the UI can say "you're 65 kr
// short" while the guest can still do something about it.
//
// The AUTHORITATIVE gate is the backend's: /woltDelivery/check compares the
// `totalAmount` we post against the same field and refuses to produce a quote
// below it, so an order can never be booked with Wolt without clearing it
// (heywaitr-staging src/controller/woltDelivery.js -> checkAvailability).
// This module only mirrors that arithmetic — the same `Number(x) || 0`
// coercion, the same two-decimal rounding, and equality passing — so the
// sentence the guest reads and the answer the server gives cannot disagree.
//
// The message is rendered through discountMessage() rather than a second copy
// of it: that helper is the app's template renderer for exactly this shape
// ({amount} / {missing} / {currency}, money formatted for the guest's locale),
// and the discount code's own minimum already reads as the same sentence.

import { discountMessage } from '../components/RestaurantMenu/DiscountCodeBlock';
import { computeDiscountedTotal } from './stampcard';

const currencyOf = (restaurant) => restaurant?.currency?.code || 'DKK';

const shortfallMessage = (language, minOrderValue, missing, currency) =>
  discountMessage(
    language,
    'wolt_min_order_not_met',
    { amount: minOrderValue, missing, currency },
    `Delivery requires a minimum order of ${minOrderValue} ${currency}.`,
  );

/**
 * The cart value the delivery minimum is measured against: the items, net of
 * EVERY discount the guest will be charged without — the discount code (already
 * off `itemsTotal`, which calculateTotal returns net of it), the Regulars
 * percentage and a staged reward voucher.
 *
 * Deliberately the same arithmetic the checkout total uses
 * (computeDiscountedTotal, which mirrors the server: voucher first, loyalty on
 * what is left), fed the items alone so no fee can count towards the minimum.
 * Measuring the gross cart let a guest clear the minimum on paper and then pay
 * less than it.
 *
 * @param {object} args
 * @param {number} args.itemsTotal      items subtotal, net of the discount code
 * @param {number} args.loyaltyPercent  Regulars discount, as a percent
 * @param {number} args.voucherDiscount reward value, absolute currency amount
 */
export const woltMinOrderValue = ({ itemsTotal, loyaltyPercent, voucherDiscount } = {}) =>
  computeDiscountedTotal({
    total: itemsTotal,
    grandTotal: itemsTotal,
    loyaltyPercent,
    voucherDiscount,
  }).adjustedGrandTotal;

/**
 * Does this cart clear the venue's delivery minimum?
 *
 * @param {object} restaurant  as returned by /mobile/restaurant/fetchspecific
 * @param {number} orderValue  the cart value the minimum applies to
 * @param {string} language    the guest's UI language
 * @returns {{orderValue: number, minOrderValue: number, missing: number, met: boolean, message: string}}
 *          `orderValue` is the coerced number this verdict was reached on, so
 *          the caller can post the very same one as `totalAmount` rather than
 *          re-deriving it. `met` is true when there is no minimum, so a venue
 *          that has not configured one — or a backend that does not return the
 *          field yet — behaves exactly as it did before.
 */
export const checkWoltMinOrder = (restaurant, orderValue, language) => {
  const minOrderValue = Number(restaurant?.WoltDeliveryMinOrderValue) || 0;
  const value = Number(orderValue) || 0;
  const shortfall = Math.round((minOrderValue - value) * 100) / 100;
  const met = minOrderValue <= 0 || shortfall <= 0;

  return {
    orderValue: value,
    minOrderValue,
    missing: met ? 0 : shortfall,
    met,
    message: met ? '' : shortfallMessage(language, minOrderValue, shortfall, currencyOf(restaurant)),
  };
};

/**
 * The backend's own minimum-order rejection, in the guest's language.
 *
 * It answers 400 with `{ message, minOrderValue, missing }` — the message being
 * an English string with a hardcoded "kr". Whenever those fields are present we
 * render our own sentence from them instead, which is what a guest sees if the
 * check above could not catch it first: the cart moved between the two, or this
 * build is talking to a backend whose fetchspecific does not carry the field.
 *
 * @returns {string} the localized sentence, or '' when this was not a minimum-order rejection
 */
export const woltMinOrderRejection = (data, restaurant, language) => {
  const minOrderValue = Number(data?.minOrderValue) || 0;
  if (minOrderValue <= 0) return '';

  return shortfallMessage(language, minOrderValue, Number(data?.missing) || 0, currencyOf(restaurant));
};
